// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { Component, Inject, PLATFORM_ID, NgZone } from '@angular/core';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { NavigationEnd, NavigationStart, Router, RouterOutlet } from '@angular/router';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { CmsService } from './shared/services/cms.service';
import { StorageEnum } from './shared/enums/storage.enum';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { StorageService } from './shared/services/storage.service';
import { firstValueFrom, map, Subject, takeUntil, tap } from 'rxjs';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { UtilService } from './shared/services/util.service';
import { SpinnerComponent } from './shared/components/spinner/spinner.component';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { DeviceDetectorService } from 'ngx-device-detector';
import { environment } from '../environments/environment';
import { MenuComponent } from './shared/components/menu/menu.component';
import { MobileNavComponent } from './shared/components/mobile-nav/mobile-nav.component';
import { FooterComponent } from './shared/components/footer/footer.component';
import { ChatBotComponent } from './shared/components/chat-bot/chat-bot.component';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { DEFAULT_INTERRUPTSOURCES, Idle } from '@ng-idle/core';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { AuthService } from './shared/services/auth.service';
import { Nav } from './shared/enums/nav.enum';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { Meta } from '@angular/platform-browser';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { BiometricsService } from './shared/services/biometrics.service';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { LoggingService } from './shared/services/logging.service';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { AlertsService } from './shared/services/alerts.service';
import { SplashScreen } from '@capacitor/splash-screen';
import { App, type URLOpenListenerEvent } from '@capacitor/app';
import { Capacitor, type PluginListenerHandle } from '@capacitor/core';

/**
 * Root application component for Gas South Online Account Service.
 * Manages global app initialization, routing, idle timeout handling,
 * deep link processing, and planned maintenance checks. Serves as the
 * shell containing navigation, footer, and main content outlet.
 */
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.css',
  imports: [RouterOutlet, SpinnerComponent, MenuComponent, MobileNavComponent, FooterComponent, ChatBotComponent],
})
export class AppComponent {
  title = 'Gas South Online Account Service';
  deviceInfo: any;
  env = environment;
  browser: boolean;
  isMobile = false;
  platform = Capacitor.getPlatform();
  private readonly unsubscribe = new Subject<void>();
  private appUrlOpenListener: PluginListenerHandle | null = null;

  constructor(
    private readonly alertsS: AlertsService,
    private readonly authS: AuthService,
    private readonly bioS: BiometricsService,
    private readonly cms: CmsService,
    private readonly metaS: Meta,
    private readonly ngIdle: Idle,
    private readonly router: Router,
    private readonly storage: StorageService,
    private readonly utils: UtilService,
    private readonly ngZone: NgZone,
    private readonly deviceService: DeviceDetectorService,
    @Inject(DOCUMENT) private readonly document: Document,
    @Inject(PLATFORM_ID) platformId: string,
    private readonly loggingService: LoggingService,
  ) {
    this.browser = isPlatformBrowser(platformId);
    if (this.browser && this.platform === 'android') {
      this.document.body.classList.add('android-system-bars');
    }
  }

  /**
   * Initializes the application on startup.
   * Checks for planned maintenance, processes deep links, sets up idle timeout,
   * initializes device info and push notifications, and loads global CMS variables.
   */
  async ngOnInit(): Promise<void> {
    this.checkPlannedMaintenance();
    if (this.browser) {
      // Ensure local storage cache is ready before any reads (native platforms)
      await this.storage.whenReady();

      this.loggingService.trackPageView(document.title, window.location.href);
      this.storage.removeLocal(StorageEnum.BioModalShown);
      await this.initializeDeepLinkListener();

      // Deep link handling is now done by deepLinkGuard in app.routes.ts

      // Check if they are coming back from an earlier process
      const pathOnly = window.location.pathname + window.location.search;
      const loginRes = this.storage.getSession(StorageEnum.LoginResponse);
      if (pathOnly !== '/login' && pathOnly !== '/' && this.utils.hasLoggedIn()) {
        const startedProcess = JSON.parse(this.storage.getLocal(StorageEnum.isProcess));
        if (!this.utils.isNullOrUndefined(startedProcess) && startedProcess.StartedProcess) {
          const stepOneUrl = Nav.ManageAccount + '/' + startedProcess.ProcessType;
          if (pathOnly.includes(startedProcess.ProcessType) && pathOnly !== stepOneUrl) {
            if (loginRes.TotalNumAccounts > 1) {
              void this.router.navigateByUrl(Nav.MultiAccountSummary);
              this.storage.removeLocal(StorageEnum.isProcess);
            } else {
              void this.router.navigateByUrl(Nav.AccountSummary);
              this.storage.removeLocal(StorageEnum.isProcess);
            }
          }
        }
      }
    }
    this.router.events.pipe(takeUntil(this.unsubscribe)).subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.utils.showSpinner();
      }
      if (event instanceof NavigationEnd) {
        if (this.browser) {
          // Track every completed navigation in ONE place
          const url = window.location.origin + event.urlAfterRedirects;
          this.loggingService.trackPageView(document.title, url);
        }
        if (this.utils.plannedMaintenance()) {
          void this.router.navigateByUrl('/site-unavailable');
        } else {
          if (event.urlAfterRedirects.includes(Nav.Login)) {
            this.ngIdle.stop();
          } else {
            this.setIdle();
          }
        }
        // clear start move reconnect storage if user navigates away from that flow
        if (this.browser) {
          const currentURL = event.urlAfterRedirects;
          if (!this.isStartMoveReconnectURL(currentURL)) {
            if (!this.utils.isNullOrUndefined(this.storage.getSession(StorageEnum.SSMData))) {
              this.storage.removeSession(StorageEnum.SSMData);
            }
          }
        }
      }
    });
    const res = await firstValueFrom(
      this.cms.getContent('global-variables', 'Global Variables').pipe(
        map((data) => {
          const copyData = JSON.parse(JSON.stringify(data));
          const errors: any[] = [];
          const allErrors: any[] = [];

          data.apiErrors.forEach((err: any) => {
            errors.push({ apiError: err.apiError.value.data });
            allErrors.push({ error: err.apiError.value.data });
          });
          copyData.apiErrors = errors;

          const paymentusErrors: any[] = [];
          data.paymentusErrors.forEach((err: any) => {
            paymentusErrors.push({ paymentusError: err.paymentusError.value.data });
            allErrors.push({ error: err.paymentusError.value.data });
          });
          copyData.paymentusErrors = paymentusErrors;

          const paymentErrors: any[] = [];
          data.paymentErrors.forEach((err: any) => {
            paymentErrors.push({ paymentError: err.paymentError.value.data });
            allErrors.push({ error: err.paymentError.value.data });
          });
          copyData.paymentErrors = paymentErrors;

          const autoPayErrors: any[] = [];
          data.autoPayErrors.forEach((err: any) => {
            autoPayErrors.push({ autoPayError: err.error.value.data });
            allErrors.push({ error: err.error.value.data });
          });
          copyData.autoPayErrors = autoPayErrors;

          const bioErrors: any[] = [];
          data.biometricsErrors.forEach((err: any) => {
            bioErrors.push(err.error.value.data);
            allErrors.push({ error: err.error.value.data });
          });
          copyData.bioErrors = bioErrors;
          this.bioS.setBioErrors(bioErrors);

          copyData.genericApiError = data.genericApiError.value.data;
          copyData.allErrors = allErrors;
          copyData.bioTypes = this.cms.flattenBuilderReferencesPreservingKeys(data.bioTypes);
          this.bioS.setBioTypeText(copyData.bioTypes);
          return copyData;
        }),
        tap((data: any) => {
          // Meta tag stuff
          if (this.env.isProduction) {
            this.metaS.removeTag('name="robots"');
          }

          if (this.browser) {
            // if no timer val default to 20min/5min
            const idleTimer = data?.idleTimer ? data.idleTimer * 60 : 20 * 60;
            const idleTimeout = data?.idleTimeout ? data.idleTimeout * 60 : 5 * 60;

            this.ngIdle.setIdle(idleTimer);
            this.ngIdle.setTimeout(idleTimeout);
            this.ngIdle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

            this.ngIdle.onIdleStart.subscribe(() => {
              this.utils.consoleLog('User is idle.');
            });
            this.ngIdle.onIdleEnd.subscribe(() => {
              this.utils.consoleLog('User is no longer idle');
            });
            this.ngIdle.onTimeout.subscribe(() => {
              this.utils.consoleLog('User is timed out');
              this.authS.logout();
            });
          }
        }),
      ),
    );

    if (this.browser) {
      this.storage.setSession(StorageEnum.GlobalVars, res);
      // This service does not actually return if device is mobile or not.
      // still needed for browser info it provides.
      this.deviceInfo = this.deviceService.getDeviceInfo();
      this.deviceInfo = { ...this.deviceInfo, deviceId: null };
      this.env.deviceInfo = this.deviceInfo;
      this.utils.consoleGroup('Device Info: ', this.deviceInfo);

      try {
        await this.alertsS.GetDeviceInfo();
        // Initialize push notification listeners early for non-web platforms
        // so we can handle notification taps and foreground notifications
        await this.alertsS.initializePushNotificationListeners();
      } catch (error) {
        this.utils.consoleGroup('Error getting device info: ', error);
      }

      this.isMobile = this.utils.isMobile(992);
      window.addEventListener('resize', () => {
        this.isMobile = this.utils.isMobile(992);
      });

      await SplashScreen.hide();
    }
  }

  /**
   * Starts the idle timeout watcher for session management.
   * Monitors user activity and triggers logout after configured idle period.
   */
  setIdle(): void {
    this.utils.consoleLog('starting ngIdle');
    this.ngIdle.watch();
  }

  /**
   * Displays the global loading spinner overlay.
   */
  showSpinner(): void {
    this.utils.showSpinner();
  }

  checkPlannedMaintenance(): void {
    this.cms.getContent('oas-maintenance-toggle', 'Maintenance Page Toggle').subscribe((toggleData: any) => {
      if (!this.utils.isNullOrUndefined(toggleData)) {
        const data = this.cms.flattenBuilderReferencesPreservingKeys(toggleData);
        if (data.plannedMaintenance) {
          this.utils.plannedMaintenance.set(true);
        }
      }
    });
  }

  ngOnDestroy(): void {
    void this.appUrlOpenListener?.remove();
    if (this.browser) {
      this.document.body.classList.remove('android-system-bars');
    }
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  private async initializeDeepLinkListener(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    let lastDeepLinkUrl: string | null = null;
    let lastDeepLinkTime: number = 0;

    this.appUrlOpenListener = await App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      const deepLinkUrl = this.toInternalDeepLinkUrl(event.url);
      if (!deepLinkUrl) {
        this.loggingService.logInfo('Deep link ignored: unable to normalize incoming URL');
        return;
      }

      if (this.shouldIgnoreDeepLink(deepLinkUrl)) {
        this.loggingService.logInfo(`Deep link ignored: non-actionable route (${deepLinkUrl})`);
        return;
      }

      // Guard against duplicate rapid appUrlOpen events (can occur with fast browser redirects)
      const now = Date.now();
      if (deepLinkUrl === lastDeepLinkUrl && now - lastDeepLinkTime < 1000) {
        this.loggingService.logInfo(`Deep link ignored: duplicate received within 1s (url=${deepLinkUrl})`);
        return;
      }

      lastDeepLinkUrl = deepLinkUrl;
      lastDeepLinkTime = now;

      const callbackReturnRoute = this.getCallbackReturnRoute(deepLinkUrl);
      if (callbackReturnRoute) {
        this.loggingService.logInfo(`Digital payment callback returning user to: ${callbackReturnRoute}`);
        this.storage.removeSession(StorageEnum.DigitalPaymentReturnRoute);
        this.ngZone.run(() => {
          void this.router.navigateByUrl(callbackReturnRoute);
        });
        return;
      }

      if (this.isDigitalPaymentCallback(deepLinkUrl) && this.isAuthenticatedSession()) {
        const fallbackRoute = this.getAuthenticatedFallbackRoute();
        this.loggingService.logInfo(`Digital payment callback fallback route used: ${fallbackRoute}`);
        this.ngZone.run(() => {
          void this.router.navigateByUrl(fallbackRoute);
        });
        return;
      }

      this.loggingService.logInfo(`Deep link received and normalized to route: ${deepLinkUrl.split('?')[0]}`);
      this.ngZone.run(() => {
        void this.router.navigateByUrl(deepLinkUrl);
      });
    });
  }

  private toInternalDeepLinkUrl(url?: string): string | null {
    if (!url) {
      return null;
    }

    try {
      const parsedUrl = new URL(url);
      const callbackPath = this.getDeepLinkPath(parsedUrl);
      return `${callbackPath}${parsedUrl.search}`;
    } catch {
      return null;
    }
  }

  private getDeepLinkPath(parsedUrl: URL): string {
    if (parsedUrl.pathname && parsedUrl.pathname !== '/') {
      return parsedUrl.pathname;
    }

    if (parsedUrl.host) {
      return `/${parsedUrl.host}`;
    }

    return '/';
  }

  private getCallbackReturnRoute(deepLinkUrl: string): string | null {
    if (!this.isDigitalPaymentCallback(deepLinkUrl)) {
      return null;
    }

    const storedReturnRoute = this.storage.getSession(StorageEnum.DigitalPaymentReturnRoute);
    if (typeof storedReturnRoute !== 'string' || !storedReturnRoute.trim()) {
      return null;
    }

    const trimmedRoute = storedReturnRoute.trim();
    return trimmedRoute.startsWith('/') ? trimmedRoute : `/${trimmedRoute}`;
  }

  private getPathWithoutQuery(url: string): string {
    const queryIndex = url.indexOf('?');
    return queryIndex >= 0 ? url.substring(0, queryIndex) : url;
  }

  private isDigitalPaymentCallback(url: string): boolean {
    return this.getPathWithoutQuery(url).toLowerCase() === environment.digitalPaymentCallbackPath.toLowerCase();
  }

  private isAuthenticatedSession(): boolean {
    return (
      !this.utils.isNullOrUndefined(this.storage.getSession(StorageEnum.LoginResponse)) ||
      !this.utils.isNullOrUndefined(this.storage.getSession(StorageEnum.MultiAccountResponse))
    );
  }

  private getAuthenticatedFallbackRoute(): string {
    const currentRoute = this.router.url;
    if (currentRoute && currentRoute !== '/' && currentRoute !== '/login') {
      return currentRoute;
    }

    return Nav.AccountSummary;
  }

  private shouldIgnoreDeepLink(deepLinkUrl: string): boolean {
    return this.getPathWithoutQuery(deepLinkUrl) === '/';
  }

  isStartMoveReconnectURL(url: string): boolean {
    return (
      url.includes(Nav.StartServiceAddress) ||
      url.includes(Nav.StartServiceScheduleService) ||
      url.includes(Nav.StartServiceChooseRates) ||
      url.includes(Nav.MoveService) ||
      url.includes(Nav.MoveServiceAddress) ||
      url.includes(Nav.MoveServiceScheduleService) ||
      url.includes(Nav.MoveServiceChooseRates) ||
      url.includes(Nav.ReconnectRates) ||
      url.includes(Nav.ReconnectService)
    );
  }
}
